import { useState } from 'react'
import { GitCompare, Copy } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function TextDiff() {
  const [text1, setText1] = useState('')
  const [text2, setText2] = useState('')
  const [diffResult, setDiffResult] = useState(null)

  const calculateDiff = () => {
    if (!text1.trim() && !text2.trim()) {
      alert('Please enter text in at least one field')
      return
    }

    const lines1 = text1.split('\n')
    const lines2 = text2.split('\n')
    const maxLines = Math.max(lines1.length, lines2.length)
    const diff = []

    for (let i = 0; i < maxLines; i++) {
      const line1 = lines1[i] || ''
      const line2 = lines2[i] || ''

      if (line1 === line2) {
        diff.push({ type: 'same', line1, line2, lineNum: i + 1 })
      } else {
        if (line1 && line2) {
          diff.push({ type: 'modified', line1, line2, lineNum: i + 1 })
        } else if (line1) {
          diff.push({ type: 'removed', line1, line2: '', lineNum: i + 1 })
        } else {
          diff.push({ type: 'added', line1: '', line2, lineNum: i + 1 })
        }
      }
    }

    setDiffResult(diff)
  }

  const clearAll = () => {
    setText1('')
    setText2('')
    setDiffResult(null)
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <Card.Body>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-3">Text 1 (Original):</label>
              <textarea
                value={text1}
                onChange={(e) => setText1(e.target.value)}
                className="input w-full min-h-[300px]"
                placeholder="Enter or paste first text here..."
              />
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-3">Text 2 (Modified):</label>
              <textarea
                value={text2}
                onChange={(e) => setText2(e.target.value)}
                className="input w-full min-h-[300px]"
                placeholder="Enter or paste second text here..."
              />
            </div>
          </Card.Body>
        </Card>
      </div>

      <Card>
        <Card.Body>
          <div className="flex gap-4">
            <Button onClick={calculateDiff} variant="primary" className="flex-1">
              <GitCompare className="w-5 h-5 mr-2" />
              Compare Texts
            </Button>
            <Button onClick={clearAll} variant="outline">
              Clear All
            </Button>
          </div>
        </Card.Body>
      </Card>

      {diffResult && (
        <Card>
          <Card.Body>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Differences:</h3>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {diffResult.map((item, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${
                    item.type === 'same'
                      ? 'bg-gray-50 border-gray-200'
                      : item.type === 'removed'
                      ? 'bg-red-50 border-red-200'
                      : item.type === 'added'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-xs font-mono text-gray-500 w-8 flex-shrink-0">
                      {item.lineNum}
                    </span>
                    <div className="flex-1">
                      {item.type === 'same' && (
                        <div className="text-gray-700">{item.line1}</div>
                      )}
                      {item.type === 'removed' && (
                        <div className="text-red-700 line-through">{item.line1}</div>
                      )}
                      {item.type === 'added' && (
                        <div className="text-green-700">{item.line2}</div>
                      )}
                      {item.type === 'modified' && (
                        <div>
                          <div className="text-red-700 line-through mb-1">{item.line1}</div>
                          <div className="text-green-700">{item.line2}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 rounded"></div>
                <span className="text-gray-600">Same</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-200 rounded"></div>
                <span className="text-gray-600">Removed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-200 rounded"></div>
                <span className="text-gray-600">Added</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-200 rounded"></div>
                <span className="text-gray-600">Modified</span>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {!diffResult && (
        <Card>
          <Card.Body className="text-center py-12">
            <GitCompare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Enter two texts and click "Compare Texts" to see differences</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

